import dayjs from "dayjs";

/** A zone the server's string already carries - then nothing is appended. */
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * A server date in the player's own timezone, MM/DD/YYYY (#199).
 *
 * The server sends UTC without a zone, so "Z" is appended before parsing - the same `+ "Z"` every
 * other screen uses - and dayjs shows it in local time. Near midnight that can be a different day
 * from the UTC one, which is the point: the date is the player's.
 *
 * @param {string} value An ISO date-time from the API.
 * @returns {string} "-" when there is none.
 */
export const formatLocalDate = (value) => {
  if (!value) return "-";
  const text = String(value);
  return dayjs(HAS_ZONE.test(text) ? text : `${text}Z`).format("MM/DD/YYYY");
};

/**
 * The confirmation of a deletion request, worded here so the date is local.
 *
 * @param {string} deletionDate `deletionDate` of Player/RequestAccountDeletion's answer.
 */
export const deletionRequestedMessage = (deletionDate) =>
  `Your account will be deleted on ${formatLocalDate(deletionDate)}.`;

/**
 * #234: the errorCode values of Account/Login's and Account/CancelAccountDeletion's refusals that
 * the sign-in page acts on.
 */
export const DELETION_ERROR = {
  pending: "ACCOUNT_PENDING_DELETION",
  inProgress: "DELETION_IN_PROGRESS",
  invalidCredentials: "INVALID_CREDENTIALS",
};

/**
 * #234: whether a sign-in refusal is the one for an account with a deletion request, which the
 * sign-in page answers with the deletion notice. `deletionDueAt` counts too: it is only ever set on
 * that refusal.
 *
 * @param {object} data The body of Account/Login's answer.
 */
export const isPendingDeletionRefusal = (data) =>
  data?.status !== true && (data?.errorCode === DELETION_ERROR.pending || !!data?.deletionDueAt);

/**
 * #234 AC1: the deletion notice, with the date in the player's timezone.
 *
 * @param {string} deletionDueAt `deletionDueAt` of the sign-in refusal.
 */
export const deletionNoticeMessage = (deletionDueAt) =>
  `Your account is scheduled for deletion on ${formatLocalDate(deletionDueAt)}. After this date your ` +
  "account and data will be permanently deleted. If you want to keep your account, you can cancel the deletion.";

export const DELETION_IN_PROGRESS_MESSAGE =
  "Your account deletion is already in progress and can no longer be cancelled.";
export const INCORRECT_PASSWORD_MESSAGE = "Incorrect password. Please try again.";
export const CANCEL_DELETION_FAILED_MESSAGE = "We couldn't cancel your deletion request. Please try again.";
export const DELETION_CANCELLED_MESSAGE = "Your deletion request has been cancelled. Your account is active.";
export const DELETION_CANCELLED_SIGN_IN_FAILED_MESSAGE =
  "Your deletion request has been cancelled, but signing you in did not finish. Please try again.";
export const AUTO_RENEW_OFF_MESSAGE =
  "Your subscription will not renew automatically. You can turn auto-renewal back on in Account Settings.";

/**
 * #234 AC13: whether to tell a player who has just cancelled their deletion that their subscription
 * no longer renews. Only while there is something to turn back on: a Stripe subscription that is
 * still running and set to end with its period - exactly when Account Settings offers "Resume
 * auto-renewal". A subscription the request cancelled outright (one whose payment had failed), or
 * one whose period ran out during the grace period, has nothing to resume.
 *
 * @param {object|null} subscription A row from pickCurrentSubscription().
 */
export const showsAutoRenewNotice = (subscription) =>
  subscription?.isStripeSubscription === true &&
  subscription?.willCancelAtPeriodEnd === true &&
  subscription?.isActive === true;

/**
 * #234: what the deletion notice's dialog says when Account/CancelAccountDeletion did not cancel.
 *
 * @param {object|undefined} res The axios response, undefined when there was no reply.
 * @returns {{ message: string, inProgress: boolean, clearPassword: boolean }}
 */
export const cancelDeletionFailure = (res) => {
  const code = res?.data?.errorCode;
  if (code === DELETION_ERROR.inProgress) {
    return { message: DELETION_IN_PROGRESS_MESSAGE, inProgress: true, clearPassword: true };
  }
  if (code === DELETION_ERROR.invalidCredentials) {
    return { message: INCORRECT_PASSWORD_MESSAGE, inProgress: false, clearPassword: true };
  }
  // A refusal the server explained (a blocked account) keeps its own sentence. No reply, a 5xx or a
  // body without a message is a failure to try again (AC17).
  if (res?.status === 200 && res?.data?.status === false && res?.data?.message) {
    return { message: res.data.message, inProgress: false, clearPassword: true };
  }
  return { message: CANCEL_DELETION_FAILED_MESSAGE, inProgress: false, clearPassword: false };
};
